// SampleHeaderCard.tsx
import { MetaField, MetaGrid } from '@/components/DetailPage';
import QCBadge from '@/components/UI/QCBadge';
import { useDateFormatter } from '@/hooks';
import detailHeaderStyles from '@/styles/components/details.page.module.css';
import { type QCOutcomeType, type SampleWithNamesPublic, speciesLabel } from '@/types';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './SampleHeaderCard.module.css';

export interface SampleHeaderCardProps {
  sample: SampleWithNamesPublic;
}

function SampleHeaderCardComponent({ sample }: SampleHeaderCardProps) {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const empty = t('empty', { ns: 'general' });

  // Each cell now maps to a real, non-nullable QCOutcomeType field — the old guessed
  // mapping (image_qc/run_qc standing in, controls_qc hardcoded) is gone.
  const qcCells: { label: string; status: QCOutcomeType }[] = [
    { label: t('samples.details.qcSampleQuality'), status: sample.sample_quality_qc },
    { label: t('samples.details.qcDecode'), status: sample.decode_qc },
    { label: t('samples.details.qcLane'), status: sample.lane_qc },
    { label: t('samples.details.qcControls'), status: sample.controls_qc },
  ];

  return (
    <Stack className={clsx(styles.card, detailHeaderStyles.header)} gap={4} orientation="vertical">
      <Stack alignItems="start" fullWidth justifyContent="space-between" orientation="horizontal">
        <Text as="h1" size="xl">
          {sample.name}
        </Text>
        {sample.assay_friendly_name !== null &&
          sample.assay_friendly_name !== undefined &&
          sample.assay_friendly_name !== '' && (
            <Text as="span" size="lg">
              {sample.assay_friendly_name}
            </Text>
          )}
      </Stack>

      <Grid fullWidth gap={6} gridTemplateColumns="2fr 1fr">
        <MetaGrid columns={2}>
          <MetaField
            label={t('samples.details.fieldSpecies')}
            placeholder={empty}
            value={sample.species ? speciesLabel(sample.species) : undefined}
          />
          <MetaField label={t('samples.details.fieldSampleGroup')} placeholder={empty} value={sample.sample_group} />
          <MetaField
            label={t('samples.details.fieldModified')}
            placeholder={empty}
            value={
              sample.updated_at !== null && sample.updated_at !== undefined ? formatDate(sample.updated_at) : undefined
            }
          />
          <MetaField label={t('samples.details.fieldCreated')} value={formatDate(sample.created_at)} />
          <MetaField
            label={t('samples.details.fieldStudy')}
            value={
              <Link params={{ studyId: sample.study_id }} to="/studies/$studyId">
                {sample.study_name}
              </Link>
            }
          />
          <MetaField
            label={t('samples.details.fieldOrganisation')}
            value={
              <Link params={{ orgId: sample.org_id }} to="/organizations/$orgId">
                {sample.org_name}
              </Link>
            }
          />
        </MetaGrid>

        <Stack gap={4} orientation="vertical">
          <Grid gap={3} gridTemplateColumns="repeat(4, 1fr)">
            {qcCells.map(cell => (
              <Stack className={styles.qcCell} key={cell.label}>
                <Text as="span" className={styles.qcLabel} size="xs">
                  {cell.label}
                </Text>
                <QCBadge qc={cell.status} />
              </Stack>
            ))}
          </Grid>

          {/* Replaces the old free-text message box. `message` was dropped as a dead column
              (100% empty, never written), so this surfaces the data that does exist: the
              collapsed overall outcome and the row-level warning flag. */}
          <Stack className={styles.messageBox} gap={1} orientation="vertical">
            <Text as="span" size="xs">
              {t('samples.details.qcOverall')}
            </Text>
            <Stack alignItems="center" gap={3} orientation="horizontal">
              <QCBadge qc={sample.sample_qc_outcome} />
              {sample.warning && (
                <Text as="span" size="sm">
                  {t('columnHeaders.warning')}
                </Text>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Grid>
    </Stack>
  );
}

export const SampleHeaderCard = memo(SampleHeaderCardComponent);
